import { useState, useEffect } from 'react';
import { RefreshCw, X, Download, Sparkles } from 'lucide-react';
import usePWA from '../hooks/usePWA';

interface UpdateNotificationProps {
  className?: string;
}

/**
 * Component that shows a notification when a new version of the app is available.
 * Allows users to update immediately or dismiss and update later.
 */
export function UpdateNotification({ className = '' }: UpdateNotificationProps) {
  const { isUpdateAvailable, isUpdating, updateApp, dismissUpdate } = usePWA();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isUpdateAvailable) {
      // Slight delay to let the page settle before showing notification
      const timer = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(timer);
    }
    setShow(false);
  }, [isUpdateAvailable]);

  if (!show) {
    return null;
  }

  const handleUpdate = async () => {
    await updateApp();
  };

  const handleDismiss = () => {
    setShow(false);
    dismissUpdate();
  };

  return (
    <div
      className={`fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-50 ${className}`}
      role="alert"
      aria-live="polite"
      aria-labelledby="update-notification-title"
    >
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
        {/* Progress bar animation during update */}
        {isUpdating && (
          <div className="h-1 bg-white/20">
            <div className="h-full bg-white animate-pulse w-full" />
          </div>
        )}

        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <h3
                id="update-notification-title"
                className="text-lg font-semibold text-white mb-1"
              >
                Update Available
              </h3>
              <p className="text-sm text-white/80 mb-4">
                A new version of the app is ready. Update now to get the latest features and improvements.
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={handleUpdate}
                  disabled={isUpdating}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-white text-blue-600 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label={isUpdating ? 'Updating application...' : 'Update application now'}
                >
                  <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
                  {isUpdating ? 'Updating...' : 'Update Now'}
                </button>
                <button
                  onClick={handleDismiss}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-white/15 text-white rounded-lg font-medium text-sm hover:bg-white/25 transition-colors disabled:opacity-50"
                  aria-label="Dismiss update notification"
                >
                  Later
                </button>
              </div>
            </div>

            <button
              onClick={handleDismiss}
              disabled={isUpdating}
              className="flex-shrink-0 text-white/60 hover:text-white transition-colors disabled:opacity-50"
              aria-label="Close update notification"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact update button for headers or toolbars
 */
export function UpdateButton({ className = '' }: { className?: string }) {
  const { isUpdateAvailable, isUpdating, updateApp } = usePWA();

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <button
      onClick={() => updateApp()}
      disabled={isUpdating}
      className={`inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-full text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 ${className}`}
      aria-label={isUpdating ? 'Updating...' : 'Update available - click to update'}
    >
      <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
      {isUpdating ? 'Updating...' : 'Update'}
    </button>
  );
}

/**
 * Badge indicator for update availability
 */
export function UpdateBadge({ className = '' }: { className?: string }) {
  const { isUpdateAvailable } = usePWA();

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center justify-center w-2 h-2 bg-blue-500 rounded-full animate-pulse ${className}`}
      aria-label="Update available"
    />
  );
}

/**
 * Full-screen update overlay for critical updates
 */
export function UpdateOverlay({ className = '' }: { className?: string }) {
  const { isUpdateAvailable, isUpdating, updateApp } = usePWA();
  const [showOverlay, setShowOverlay] = useState(false);

  // Only show overlay after a delay if update is not installed
  useEffect(() => {
    if (isUpdateAvailable) {
      const timer = setTimeout(() => setShowOverlay(true), 30000); // 30 seconds
      return () => clearTimeout(timer);
    }
    setShowOverlay(false);
  }, [isUpdateAvailable]);

  if (!showOverlay) {
    return null;
  }

  return (
    <div
      className={`fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-overlay-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Download className="w-8 h-8 text-blue-600" />
        </div>

        <h2
          id="update-overlay-title"
          className="text-xl font-bold text-gray-900 mb-2"
        >
          Update Required
        </h2>
        <p className="text-gray-600 mb-6">
          A new version of MM English Assessment is available. Please update to continue using the app with the latest features.
        </p>

        <button
          onClick={() => updateApp()}
          disabled={isUpdating}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-5 h-5 ${isUpdating ? 'animate-spin' : ''}`} />
          {isUpdating ? 'Updating...' : 'Update Now'}
        </button>
      </div>
    </div>
  );
}

export default UpdateNotification;
